import React, { useMemo } from 'react';
import { Box, Stack, Typography, LinearProgress, ProfileAvatar, Tooltip } from '../../../ui/core';
import { hairline, rowHover } from '../../../ui/styles';
import { useFormatters } from '../../../hooks';
import { TripStatus, TripDisplayStatusOrder } from '../../../models/Trips';
import { isGhostTrip, getPairedLeg } from '../../../helpers/utils/trip';
import { getExcludedRiderIds } from '../../../helpers/utils/riderDriverExclusions';
import { StationIcon, LuggageIcon, DollarBadgeIcon, CarIcon, WarningIcon } from '../../../ui/icons';
import CheckCircleIcon from '../../../ui/icons/CheckCircleIcon';
import CircleIcon from '../../../ui/icons/CircleIcon';
import DottedCircleIcon from '../../../ui/icons/DottedCircleIcon';
import SearchCircleIcon from '../../../ui/icons/SearchCircleIcon';
import TripTags from './TripTags';
import TripETA from './TripETA';
import { TripDisplayStatusIcon } from './TripDisplayStatus';
import { MOBILE_BAR_H } from '../../../layouts/DashoardLayout';

// The phone/tablet rendering of the daily trips list.
//
// The desktop view is an eleven-column table roughly 1,400px wide. Narrowing that
// is not a fix — every column either truncates to uselessness or forces the reader
// to scrub sideways to assemble a single trip. So below `md` each trip becomes a
// card that carries the same facts stacked vertically, grouped under the same
// status sections (and in the same order) as the table's group headers, with the
// same tap target: opening the trip.
//
// A card does NOT repeat its own status: the section header above it is sticky, so
// the status is on screen the whole time you're reading that section, and printing it
// again in every row was the same word twice. (The table needs its Status column
// because a scrolled grid row can be far from its group header.) Tags are not status
// and stay on the card.
//
// Round trips: the table draws the paired outbound/return leg as a greyed companion
// row joined to its partner by a vertical dotted line. That relationship carries over
// here as a greyed leg attached to the bottom of the card behind the same dashed
// connector, so a paired trip still reads as one booking on a phone. Same pairing
// rules as the table (see getPairedLeg), so the two surfaces agree about what's
// paired with what.

const renderConfirmationStatusIcon = (status) => {
  const sx = { fontSize: 15 };
  let icon = null;
  let label = null;
  switch (status) {
    case 'PRE_CONFIRMATION':
      icon = <DottedCircleIcon sx={{ ...sx, color: 'text.disabled' }} />;
      label = 'Pre-Confirmation';
      break;
    case 'CONFIRMED':
      icon = <CheckCircleIcon color="success" sx={sx} />;
      label = 'Confirmed';
      break;
    case 'UNCONFIRMED':
      icon = <CircleIcon sx={{ ...sx, color: 'text.disabled' }} />;
      label = 'Unconfirmed';
      break;
    case 'PENDING_REASSIGNMENT':
      icon = <SearchCircleIcon color="error" sx={sx} />;
      label = 'Pending Reassignment';
      break;
    default:
      return null;
  }
  return (
    <Tooltip title={label} arrow>
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        {icon}
      </Box>
    </Tooltip>
  );
};

// HITCH-5300: the grid flags a trip whose driver is excluded with one of its riders,
// on the Driver cell. Carried over here rather than dropped — it's a safety signal, and
// a phone shouldn't be the surface that silently stops showing it.
const renderExclusionWarningIcon = (trip, excludedRiderIds) => {
  if (!excludedRiderIds || excludedRiderIds.size === 0) return null;
  const names = (trip.rides || [])
    .filter((ride) => excludedRiderIds.has(ride.rider.id))
    .map((ride) => `${ride.rider.firstName} ${ride.rider.lastName}`.trim())
    .join(', ');
  return (
    <Tooltip title={`Excluded pairing with ${names} — see Rider-Driver Exclusions.`} arrow>
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <WarningIcon sx={{ fontSize: 15, color: 'warning.main' }} />
      </Box>
    </Tooltip>
  );
};

const metaSx = { display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 };
const metaIconSx = { fontSize: 15, color: 'text.secondary' };

// The dashed rail joining a trip to its paired leg — the card equivalent of the
// table's TimeRangeVisual (hollow ring at the first departure, dashed line, filled
// dot at the second). Drawn from theme colors rather than TimeRangeVisual's
// hardcoded greys, which only read correctly in light mode.
// The rail joining a trip to its paired leg — the card equivalent of the table's
// TimeRangeVisual (hollow ring at the first departure, dashed line, filled dot at the
// second). Absolutely positioned down the card's left gutter so it actually SPANS the
// two legs; drawn inside the paired block it only decorated that block and tied
// nothing to anything.
//
// The two offsets are the distance from each edge of the card to the CENTRE of the
// departure row it marks, and both are sums of this component's own fixed metrics:
//   top    = card padding (13) + half a 24px time row
//   bottom = card padding (13) + route row (18) + its 2px margin + half the 24px
//            paired time row
// The desktop TimeRangeVisual pins itself with the same kind of constants; if the card
// type sizes change, these move with them.
const RAIL_TOP = 25;
const RAIL_BOTTOM = 45;
// Width of the gutter the rail sits in, which the card's content is inset by.
const RAIL_GUTTER = 18;

const PairConnector = () => (
  <Box
    aria-hidden
    sx={{
      position: 'absolute',
      left: '16px',
      top: `${RAIL_TOP}px`,
      bottom: `${RAIL_BOTTOM}px`,
      width: '8px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        border: (theme) => `2px solid ${theme.palette.text.primary}`,
        flexShrink: 0,
      }}
    />
    <Box
      sx={{
        flex: 1,
        minHeight: '10px',
        width: 0,
        my: '2px',
        borderLeft: (theme) => `2px dashed ${theme.palette.text.inactive}`,
      }}
    />
    <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'text.secondary', flexShrink: 0 }} />
  </Box>
);

// A trip's other half. Greyed like the table's companion rows, and carrying the same
// facts those rows show: departure, route, stop count, payout.
const PairedLeg = ({ leg, relation }) => {
  const f = useFormatters();
  return (
    // No top divider: a rule across the card would cut the rail that is the whole
    // point of this block. The greying and the label carry the separation instead.
    <Box sx={{ color: 'text.secondary' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" spacing="8px">
          <Typography sx={{ fontSize: '12.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            {relation === 'next' ? 'Return' : 'Outbound'}
          </Typography>
          <Typography sx={{ fontSize: '14px', flex: 1, minWidth: 0 }}>
            {f.date.toTimezoneLocaleString(
              leg.departureTime,
              leg.originCity.timeZone,
              f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
            )}
          </Typography>
          <Typography sx={{ fontSize: '14px' }}>{f.number.currency(leg.payout || 0)}</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing="12px" sx={{ mt: '2px', flexWrap: 'wrap', rowGap: '2px' }}>
          <Typography sx={{ fontSize: '13px' }}>
            {leg.originCity.cityName} ➔ {leg.destinationCity.cityName}
          </Typography>
          <Box sx={metaSx}>
            <StationIcon sx={metaIconSx} />
            <Typography sx={{ fontSize: '13px' }}>{(leg.rides || []).length}</Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

const TripCard = ({ trip, pairedLeg, excludedRiderIds, onClick }) => {
  const f = useFormatters();
  const ghost = isGhostTrip(trip);
  const manualBonus = trip.payoutItemized?.manualBonus || 0;

  const departure = f.date.toTimezoneLocaleString(
    trip.departureTime,
    trip.originCity.timeZone,
    f.date.DateFormats.TIME_SIMPLE_WITH_TIMEZONE,
  );

  const riderNames = (trip.rides || []).map((r) => `${r.rider.firstName} ${r.rider.lastName}`).join(', ');

  // Each leg is its own tap target. The shell is deliberately NOT interactive: the
  // companion leg is a different trip, and while it sat inside the primary card's
  // single button, tapping the return leg opened the outbound. The desktop grid gets
  // this right because a companion is its own row carrying its own `id`, so this is
  // parity with it, not a new behaviour. Two sibling buttons rather than one nested
  // in the other, which would also be invalid.
  const openTrip = (target) => {
    return {
      role: 'button',
      tabIndex: 0,
      onClick: () => onClick({ row: target }),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick({ row: target });
        }
      },
    };
  };

  // Paired cards inset their content to clear the rail running down the gutter.
  const gutter = pairedLeg ? `${16 + RAIL_GUTTER}px` : '16px';
  const tapSx = {
    pl: gutter,
    pr: '16px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: rowHover },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderBottom: (theme) => `1px solid ${hairline(theme)}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {pairedLeg && <PairConnector />}

      <Box
        {...openTrip(trip)}
        sx={{
          ...tapSx,
          pt: '13px',
          pb: pairedLeg ? '11px' : '13px',
        }}
      >
      {/* Time + money: the two numbers ops scans a list for, pinned to the edges. */}
      <Stack direction="row" alignItems="baseline" spacing="8px">
        <Typography sx={{ fontSize: '15px', fontWeight: 600, flex: 1, minWidth: 0 }}>
          {ghost && '👻 '}
          {departure}
        </Typography>
        {trip.status === TripStatus.in_progress && (
          <Box sx={{ fontSize: '13px', color: 'text.secondary', '.MuiTypography-root': { fontSize: '13px' } }}>
            <TripETA trip={trip} />
          </Box>
        )}
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{f.number.currency(trip.payout || 0)}</Typography>
      </Stack>

      {/* Route + the counts that qualify it */}
      <Stack direction="row" alignItems="center" spacing="12px" sx={{ mt: '3px', flexWrap: 'wrap', rowGap: '2px' }}>
        <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
          {trip.originCity.cityName} ➔ {trip.destinationCity.cityName}
        </Typography>
        <Box sx={metaSx}>
          <StationIcon sx={metaIconSx} />
          <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{(trip.rides || []).length}</Typography>
        </Box>
        {manualBonus > 0 && (
          <Box sx={metaSx}>
            <DollarBadgeIcon sx={metaIconSx} />
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              {f.number.currency(manualBonus)} bonus
            </Typography>
          </Box>
        )}
      </Stack>

      {/* Tags only — the sticky section header carries the status. Rendered solely
          when there are tags, so an untagged trip doesn't pay for an empty row. */}
      {(trip.tags || []).length > 0 && (
        <Stack direction="row" alignItems="center" sx={{ mt: '9px', flexWrap: 'wrap', rowGap: '6px' }}>
          <TripTags tags={trip.tags} />
        </Stack>
      )}

      {/* Who: driver (with confirmation state) and the riders on board */}
      {(trip.driver || riderNames || trip.fleetVehicle) && (
        <Stack spacing="6px" sx={{ mt: '10px' }}>
          {trip.driver && (
            <Stack direction="row" alignItems="center" spacing="8px">
              <ProfileAvatar
                size={24}
                name={`${trip.driver.firstName || ''} ${trip.driver.lastName || ''}`.trim()}
                colorKey={trip.driver.id}
                photoUrl={trip.driver.imageUrl}
              />
              <Typography sx={{ fontSize: '14px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {trip.driver.firstName} {trip.driver.lastName}
              </Typography>
              {renderConfirmationStatusIcon(trip.confirmationStatus)}
              {renderExclusionWarningIcon(trip, excludedRiderIds)}
            </Stack>
          )}
          {trip.fleetVehicle && (
            <Stack direction="row" alignItems="center" spacing="8px">
              <CarIcon sx={metaIconSx} />
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                {trip.fleetVehicle.displayName}
                {(trip.fleetVehicle.year || trip.fleetVehicle.make || trip.fleetVehicle.model) && ' — '}
                {[trip.fleetVehicle.year, trip.fleetVehicle.make, trip.fleetVehicle.model].filter(Boolean).join(' ')}
              </Typography>
            </Stack>
          )}
          {riderNames && (
            <Stack direction="row" alignItems="center" spacing="8px">
              <LuggageIcon sx={metaIconSx} />
              <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{riderNames}</Typography>
            </Stack>
          )}
        </Stack>
      )}
      </Box>

      {pairedLeg && (
        <Box {...openTrip(pairedLeg.trip)} sx={{ ...tapSx, pt: 0, pb: '13px' }}>
          <PairedLeg leg={pairedLeg.trip} relation={pairedLeg.relation} />
        </Box>
      )}
    </Box>
  );
};

const TripsMobileList = ({ data, allTrips, exclusionMatchSet, loading, onRowClick }) => {
  // Group into the same sections, in the same order, as the desktop grid's group
  // headers — so the status strip's counts and its scroll-to targets line up with
  // what is on screen. Empty statuses are dropped rather than rendered as bare
  // headers; the strip already shows them greyed at zero.
  const sections = useMemo(() => {
    const byStatus = new Map();
    data.forEach((t) => {
      if (!byStatus.has(t.displayStatus)) byStatus.set(t.displayStatus, []);
      byStatus.get(t.displayStatus).push(t);
    });
    const known = TripDisplayStatusOrder.filter((s) => byStatus.has(s)).map((s) => ({ status: s, trips: byStatus.get(s) }));
    // A status the order list doesn't know about would otherwise vanish silently.
    const unknown = [...byStatus.keys()]
      .filter((s) => !TripDisplayStatusOrder.includes(s))
      .map((s) => ({ status: s, trips: byStatus.get(s) }));
    return [...known, ...unknown];
  }, [data]);

  if (loading) {
    return (
      <Box sx={{ px: '16px', py: '24px' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!sections.length) {
    return (
      <Box sx={{ px: '16px', py: '32px' }}>
        <Typography sx={{ fontSize: '14px', color: 'text.secondary', textAlign: 'center' }}>
          No trips for this day.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {sections.map(({ status, trips }) => (
        <Box key={status}>
          <Stack
            direction="row"
            alignItems="center"
            spacing="8px"
            sx={{
              // Sticky directly under the layout's mobile top bar, so the status stays
              // named for as long as you're scrolling its trips — which is what lets
              // the cards drop their own status label. Each header is scoped to its own
              // section wrapper, so it unpins when that section ends and the next
              // header takes over. Requires the content card NOT to clip (see
              // pages/Trips/index.js) — a clipping ancestor becomes the scrollport and
              // sticky silently stops working.
              position: 'sticky',
              top: `${MOBILE_BAR_H}px`,
              // Above the trip cards it scrolls over, below the top bar it sits under.
              zIndex: 1,
              px: '16px',
              py: '10px',
              // Opaque, not a translucent tint: stacked sticky headers have to cover
              // one another cleanly as you scroll between sections.
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#212124' : '#F2F2F3'),
              borderBottom: (theme) => `1px solid ${hairline(theme)}`,
            }}
          >
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
              <TripDisplayStatusIcon status={status} />
            </Box>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, flex: 1, minWidth: 0 }}>{status}</Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>({trips.length})</Typography>
          </Stack>
          {trips.map((trip) => (
            <TripCard
              key={trip.id}
              trip={trip}
              pairedLeg={getPairedLeg(trip, allTrips || data)}
              excludedRiderIds={getExcludedRiderIds(
                trip.driver?.id,
                (trip.rides || []).map((ride) => ride.rider),
                exclusionMatchSet,
              )}
              onClick={onRowClick}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default TripsMobileList;
